import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { ContentContainer, Layout } from '@/components/layout';
import { getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { FaqIntro } from '@/sections/FaqIntro';
import MoreInfo from '@/sections/whereToShop/MoreInfo';
import EshopBoxes, { CmsEshopItem, cmsItemsToEshops } from '@/components/boxes/EshopBox/EshopBoxes';
import { Button } from '@/components/buttons';
import IconDown from '@/assets/icons/heroIcons/Chevron-down.svg';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import useWhereToShop, { SearchFormValues, PaymentMethodFieldValues, getBaseFilter } from '@/hooks/useWhereToShop';
import { QueryFilter } from '@/utils/query';
import React, { memo, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { EshopBox } from '@/components/boxes/EshopBox/types';
import Floating, { Boxes } from '@/sections/Floating';
import { Link } from '@/components/links';
import { LINKS } from '@/constants/links';
import Search from '@/sections/whereToShop/Search';
import { NothingFound } from '@/sections/whereToShop/NothingFound';
import { Partner, Partners } from '@/types/partners';
import { fetchPartners } from '@/api/skippay/partners';
import { useQueryStates } from 'nuqs';
import { parseAsSearchString, SELECT_ALL_VALUE } from '@/hooks/searchParamParsers';
import { FormApi, createForm } from 'final-form';
import { GetFeaturedPartnersQuery, useGetFeaturedPartnersQuery } from '@/api/sanity/generated';
import { SeoHeader } from '@/components/SeoHeader';
import { Headline } from '@/components/typography';
import { useEffectOnce } from 'react-use';
import { logEvent } from '@/analytics/events';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { sanityLanguage } from '@/constants/language';
import { useRouter } from 'next/router';
const partnersDataToEshops = ({
  data,
  imageSize,
  size,
  section,
  index
}: {
  data: Partner[];
  imageSize: EshopBox['image']['size'];
  size: EshopBox['size'];
  section: EshopBox['section'];
  index: EshopBox['index'];
}) => {
  return data.map(partner => ({
    size,
    link: partner.url,
    image: {
      url: partner.logo.url,
      title: partner.name,
      size: imageSize,
      width: 72,
      height: 57
    },
    section,
    index
  }));
};
const POPULAR = 'popular';
const FeaturedPartnersEshops = memo(function FeaturedPartnersEshops(props) {
  const $ = _c(4);
  let t0;
  if ($[0] !== props.featuredPartners?.allFeaturedPartnersList) {
    t0 = props.featuredPartners?.allFeaturedPartnersList?.map(_temp);
    $[0] = props.featuredPartners?.allFeaturedPartnersList;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <ContentContainer>{t0}</ContentContainer>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
});

// TODO: Fix this hook to support react-compiler
function useAutoScrollWithPaymentMethod() {
  const router = useRouter();
  const listRef = useRef<HTMLDivElement | null>(null);
  const hasScrolled = useRef(false);
  useEffect(() => {
    if (!router.isReady || hasScrolled.current) return;
    const meth = router.query.meth as string | undefined;
    if (meth && listRef.current) {
      listRef.current.scrollIntoView({
        behavior: 'smooth'
      });
      hasScrolled.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);
  return listRef;
}
export default function Page(t0) {
  const $ = _c(124);
  const {
    featuredPartners,
    menuPromoBanners,
    initialPartnersData
  } = t0;
  usePageView("where-to-shop");
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["core", "whereToShop"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      q: parseAsSearchString,
      cat: parseAsSearchString,
      meth: parseAsSearchString
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [searchParams, setSearchParams] = useQueryStates(t2);
  const [, startTransition] = useTransition();
  const t3 = searchParams.q ?? "";
  const t4 = searchParams.cat && searchParams.cat !== SELECT_ALL_VALUE ? searchParams.cat : SELECT_ALL_VALUE;
  const t5 = searchParams.meth as PaymentMethodFieldValues | undefined ?? undefined;
  let t6;
  if ($[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = {
      fulltext: t3,
      categoryId: t4,
      paymentMethod: t5
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const formValues = t6;
  let t7;
  if ($[6] !== router.isReady || $[7] !== searchParams.cat || $[8] !== searchParams.meth || $[9] !== searchParams.q || $[10] !== setSearchParams || $[11] !== startTransition) {
    t7 = values => {
      if (!router.isReady) {
        return;
      }
      const newQ = values.fulltext || null;
      const newCat = values.categoryId && values.categoryId !== SELECT_ALL_VALUE ? values.categoryId : null;
      const newMeth = values.paymentMethod || null;
      if (newQ === searchParams.q && newCat === searchParams.cat && newMeth === searchParams.meth) {
        return;
      }
      startTransition(() => {
        setSearchParams({
          q: newQ,
          cat: newCat,
          meth: newMeth
        });
      });
    };
    $[6] = router.isReady;
    $[7] = searchParams.cat;
    $[8] = searchParams.meth;
    $[9] = searchParams.q;
    $[10] = setSearchParams;
    $[11] = startTransition;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const sync = t7;
  let t8;
  if ($[13] !== formValues || $[14] !== sync) {
    t8 = () => createForm({
      initialValues: formValues,
      onSubmit: values_0 => {
        sync(values_0);
        return Promise.resolve(values_0);
      }
    });
    $[13] = formValues;
    $[14] = sync;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const [searchForm] = useState(t8);
  let t10;
  let t9;
  if ($[16] !== formValues || $[17] !== searchForm) {
    t9 = () => {
      if (typeof window !== "undefined") {
        searchForm.reset(formValues);
      }
    };
    t10 = [formValues, searchForm];
    $[16] = formValues;
    $[17] = searchForm;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  if ($[20] !== formValues || $[21] !== initialPartnersData) {
    t11 = {
      formValues,
      initialPartnersData
    };
    $[20] = formValues;
    $[21] = initialPartnersData;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const {
    allPartners,
    categories,
    loading,
    section
  } = useWhereToShop(t11);
  const isCsLanguage = useIsSelectedLanguage("cs");
  const isHuLanguage = useIsSelectedLanguage("hu");
  let t12;
  if ($[23] !== featuredPartners || $[24] !== startTransition) {
    t12 = () => {
      if (!featuredPartners?.allFeaturedPartnersList || featuredPartners.allFeaturedPartnersList.length === 0) {
        return;
      }
      startTransition(() => {
        const events = featuredPartners?.allFeaturedPartnersList.map(_temp2);
        events.forEach(_temp3);
      });
    };
    $[23] = featuredPartners;
    $[24] = startTransition;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  useEffectOnce(t12);
  const listRef = useAutoScrollWithPaymentMethod();
  let t13;
  if ($[26] !== t) {
    t13 = t("whereToShop:seo.title");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("whereToShop:seo.description");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = <SeoHeader title={t13} description={t14} />;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="bgCircle absolute top-0 right-0 h-[25rem] w-[25rem] translate-x-[40%] -translate-y-1/2 rotate-180 lg:h-[55.25rem] lg:w-[55.25rem] lg:translate-x-[54%] lg:-translate-y-[40%] lg:rotate-[195deg]" />;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t) {
    t17 = t("whereToShop:header");
    $[34] = t;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t17) {
    t18 = <div className="relative z-10 px-4 pt-32 lg:pt-64"><Headline as="h1" id="where-to-shop" variant="h2" className="text-center">{t17}</Headline></div>;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== featuredPartners) {
    t19 = <FeaturedPartnersEshops featuredPartners={featuredPartners} />;
    $[38] = featuredPartners;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("whereToShop:allShops.title");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20) {
    t21 = <Headline as="h2" variant="h6" className="mb-6 text-center lg:mb-8 lg:text-left">{t20}</Headline>;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const t22 = categories?.data?.data;
  const t23 = allPartners?.data?.pages[0]?.pagingInfo?.totalCount;
  let t24;
  if ($[44] !== formValues || $[45] !== searchForm || $[46] !== sync || $[47] !== t22 || $[48] !== t23) {
    t24 = <Search partnersCategories={t22} initialValues={formValues} handleSearch={sync} searchForm={searchForm} totalCount={t23} />;
    $[44] = formValues;
    $[45] = searchForm;
    $[46] = sync;
    $[47] = t22;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== allPartners.data?.pages || $[51] !== section) {
    t25 = allPartners?.data?.pages.filter(Boolean).flatMap((t26, index_0) => {
      const {
        data
      } = t26;
      return partnersDataToEshops({
        data,
        imageSize: "contain",
        size: "sm",
        section,
        index: index_0
      });
    }) || [];
    $[50] = allPartners.data?.pages;
    $[51] = section;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  const t26 = allPartners.isLoading || allPartners.isPending;
  let t27;
  if ($[53] !== t25 || $[54] !== t26) {
    t27 = <EshopBoxes eshops={t25} boxSize="wide" className="mb-8 justify-start lg:mb-12" isLoading={t26} placeholderCount={12} />;
    $[53] = t25;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== allPartners.data || $[57] !== allPartners.fetchNextPage || $[58] !== allPartners.hasNextPage || $[59] !== allPartners.isFetchingNextPage || $[60] !== loading || $[61] !== t) {
    t28 = !loading && (allPartners.data?.pages[0]?.data && allPartners.data.pages[0].data.length > 0 ? <>{allPartners.hasNextPage && <Button variant="secondary" rightIcon={<IconDown />} size="sm" align="center" onClick={allPartners.fetchNextPage} analyticsParam="whereToShopPayInThreeShowMore" testId="whereToShopPayInThreeShowMore" busy={allPartners.isFetchingNextPage}>{t("core:showMore")}</Button>}</> : <NothingFound />);
    $[56] = allPartners.data;
    $[57] = allPartners.fetchNextPage;
    $[58] = allPartners.hasNextPage;
    $[59] = allPartners.isFetchingNextPage;
    $[60] = loading;
    $[61] = t;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t27 || $[64] !== t28) {
    t29 = <div className="min-h-[600px] pt-4 lg:min-h-[400px] lg:pt-8">{t27}{t28}</div>;
    $[63] = t27;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== listRef || $[67] !== t21 || $[68] !== t24 || $[69] !== t29) {
    t30 = <ContentContainer id="all" ref={listRef}>{t21}{t24}{t29}</ContentContainer>;
    $[66] = listRef;
    $[67] = t21;
    $[68] = t24;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t19 || $[72] !== t30) {
    t31 = <div className="relative z-10 flex flex-col gap-12 px-4 py-4 pt-12 lg:gap-[7.5rem] lg:pt-24 lg:pb-[7.5rem]">{t19}{t30}</div>;
    $[71] = t19;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t18 || $[75] !== t31) {
    t32 = <div className="relative overflow-x-hidden bg-blue-50">{t16}{t18}{t31}</div>;
    $[74] = t18;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t) {
    t34 = t("howToShop.anchor");
    $[78] = t;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Link to={LINKS().payInThree} analyticsParam="whereToShopHowItWorksPayInThree" testId="whereToShopHowItWorksPayInThree" className="hover:text-white" />;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  let t36;
  let t37;
  if ($[81] !== t) {
    t36 = <Trans t={t} i18nKey="whereToShop:howItWorks.items.1.text">{t35}</Trans>;
    t37 = t("whereToShop:howItWorks.items.1.title");
    $[81] = t;
    $[82] = t36;
    $[83] = t37;
  } else {
    t36 = $[82];
    t37 = $[83];
  }
  let t38;
  if ($[84] !== isCsLanguage || $[85] !== isHuLanguage || $[86] !== t) {
    t38 = isCsLanguage ? {
      videoUrl: "/videos/cs/animation-pay-in-three.mp4"
    } : {
      image: {
        alt: t("whereToShop:howItWorks.items.1.title"),
        src: isHuLanguage ? "/images/sections/forEshops/cards/hu/1.png" : "/images/sections/whereToShop/cards/sk/1.png"
      }
    };
    $[84] = isCsLanguage;
    $[85] = isHuLanguage;
    $[86] = t;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== t36 || $[89] !== t37 || $[90] !== t38) {
    t39 = {
      anchorKey: "whereToShop.howItWorks.items.1.title",
      variant: "small",
      content: t36,
      title: t37,
      ...t38
    };
    $[88] = t36;
    $[89] = t37;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== isCsLanguage || $[93] !== t) {
    t40 = isCsLanguage && {
      anchorKey: "whereToShop.howItWorks.items.2.title",
      variant: "small",
      content: <Trans t={t} i18nKey="whereToShop:howItWorks.items.2.text"><Link to={LINKS().card} analyticsParam="whereToShopHowItWorksCard" testId="whereToShopHowItWorksCard" className="hover:text-white" /></Trans>,
      title: t("whereToShop:howItWorks.items.2.title"),
      videoUrl: "/videos/cs/animation-card.mp4",
      imagePosition: "right"
    };
    $[92] = isCsLanguage;
    $[93] = t;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== isCsLanguage || $[96] !== isHuLanguage || $[97] !== t) {
    t41 = !isHuLanguage && {
      anchorKey: "whereToShop.howItWorks.items.3.title",
      variant: "small",
      content: <Trans t={t} i18nKey="whereToShop:howItWorks.items.3.text"><Link to={LINKS().paymentGateway} analyticsParam="whereToShopHowItWorksPaymentGateway" testId="whereToShopHowItWorksPaymentGateway" className="hover:text-white" /></Trans>,
      title: t("whereToShop:howItWorks.items.3.title"),
      ...(isCsLanguage ? {
        videoUrl: "/videos/cs/animation-comgate.mp4"
      } : {
        image: {
          alt: t("whereToShop:howItWorks.items.3.title"),
          src: "/images/sections/whereToShop/cards/sk/2.png"
        },
        imagePosition: "right"
      })
    };
    $[95] = isCsLanguage;
    $[96] = isHuLanguage;
    $[97] = t;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] !== t39 || $[100] !== t40 || $[101] !== t41) {
    t42 = [t39, t40, t41].filter(Boolean);
    $[99] = t39;
    $[100] = t40;
    $[101] = t41;
    $[102] = t42;
  } else {
    t42 = $[102];
  }
  const t43 = t42 as Boxes;
  let t44;
  if ($[103] !== t) {
    t44 = t("whereToShop:howItWorks.title");
    $[103] = t;
    $[104] = t44;
  } else {
    t44 = $[104];
  }
  let t45;
  if ($[105] !== t43 || $[106] !== t44) {
    t45 = <Floating boxes={t43} title={t44} />;
    $[105] = t43;
    $[106] = t44;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <Link to="/caste-dotazy/nakupy-a-vratky" analyticsParam="faqWhereToShop" testId="faqWhereToShop" />;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] !== t) {
    t47 = <FaqIntro className="pt-14 pb-14 lg:pt-28 lg:pb-24" text={<Trans i18nKey="whereToShop:faqIntro.text" t={t}>{t46}</Trans>} />;
    $[109] = t;
    $[110] = t47;
  } else {
    t47 = $[110];
  }
  let t48;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <MoreInfo />;
    $[111] = t48;
  } else {
    t48 = $[111];
  }
  let t49;
  if ($[112] !== t34 || $[113] !== t45 || $[114] !== t47) {
    t49 = <div id={t34} className="relative z-10 pt-10 lg:pt-20">{t45}{t47}{t48}</div>;
    $[112] = t34;
    $[113] = t45;
    $[114] = t47;
    $[115] = t49;
  } else {
    t49 = $[115];
  }
  let t50;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[116] = t50;
  } else {
    t50 = $[116];
  }
  let t51;
  if ($[117] !== t49) {
    t51 = <div className="relative overflow-hidden bg-blue-100">{t49}{t50}</div>;
    $[117] = t49;
    $[118] = t51;
  } else {
    t51 = $[118];
  }
  let t52;
  if ($[119] !== menuPromoBanners || $[120] !== t15 || $[121] !== t32 || $[122] !== t51) {
    t52 = <Layout menuPromoBanners={menuPromoBanners}>{t15}{t32}{t33}{t51}</Layout>;
    $[119] = menuPromoBanners;
    $[120] = t15;
    $[121] = t32;
    $[122] = t51;
    $[123] = t52;
  } else {
    t52 = $[123];
  }
  return t52;
}
function _temp3(event) {
  return logEvent("view_promotion", {
    ecommerce: event.ecommerce
  });
}
function _temp2(item) {
  return {
    ecommerce: {
      items: item?.items?.map((eshop, index) => ({
        promotion_id: "affil_promo",
        promotion_name: eshop?.name,
        creative_name: eshop?.name,
        creative_slot: `${item.type}_${index + 1}`,
        location_id: eshop?.link
      }))
    }
  };
}
export const getStaticProps: GetStaticProps = async ctx => {
  const fetchFeaturedPartnersDocument = useGetFeaturedPartnersQuery.fetcher({
    i18nLang: sanityLanguage,
    type: 'popular'
  });
  const featuredPartners = await fetchFeaturedPartnersDocument();
  const filter = getBaseFilter(new QueryFilter());
  const initialPartnersData = await fetchPartners({
    limit: 12,
    filter
  });
  const intlProps = await getIntlProps(ctx, ['core', 'whereToShop']);
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      featuredPartners,
      initialPartnersData,
      ...intlProps
    }
  };
};
function _temp(item, index) {
  return <EshopBoxes key={item?.type as string} boxSize="md" imageSizes="(max-width: 640px) 60px, 196px" eshops={cmsItemsToEshops({
    items: item.items as CmsEshopItem[],
    maxWidth: 296,
    imageSize: "cover",
    size: "md",
    section: POPULAR,
    index
  })} loadingMethod="eager" />;
}